// ============================================================
// TERROR ENGINE — THE FOUNTAIN
// Toss a Chocolate Coin into V'ael's Plaza. Most of the time
// J'ynxi hands you a card that isn't one.
// ============================================================
//
// caP'n, July 29 2026:
//   "you can throw a chocolate coin until you get the prize for the
//    day... chance per prize, roughly 7% for Fable, 3% for
//    Legendary, and 1% for Mythical, but they're all checklist —
//    that's the good news, no duplicates."
//   "a bubble pops up as a fake card from J'ynxi saying better luck
//    next time hehe"
//   "we'll just keep making new holos ;3"
//
// THE SHAPE THAT FALLS OUT OF THOSE THREE LINES
//
// 11% of tosses hit. 89% get trolled. Every prize is a CHECKLIST
// entry — win it once and it leaves your pool forever, so the odds
// improve as the list empties and the last few are quick. One prize
// per day caps the grind and is what "the prize for the day" means.
//
// THE POOL IS A TABLE, NOT A SUBSYSTEM. "We'll just keep making new
// holos" is a promise that this list grows for the life of the game,
// so nothing here may ever switch on a prize id. A prize is four
// fields, and `kind` is a string the granter dispatches on — when
// the fountain-exclusive holo tier lands it is a `kind: "holo"` row
// and a case in one function, not an engine change. That is the
// difference between content work and a session.
//
// OWNERSHIP IS READ WHERE IT ALREADY LIVES. There is deliberately no
// `fountain/won` ledger: a backdrop you BOUGHT in the Atelier is a
// backdrop you own, and the fountain must not hand it to you a
// second time. Duplicate-checking against the real unlock paths is
// the only version of "no duplicates" that is actually true.

import { ref, get, set, runTransaction } from "firebase/database";
import { db } from "./firebase";
import { spendCoins, unlockBackdrop, unlockDrawPulse, grantCrests } from "./progression";

// ── DIALS ─────────────────────────────────────────────────────
// Balance these with a controller in hand, not in theory.
export const FOUNTAIN_COST = 15;      // ◕ per toss — THE dial to tune first
export const FOUNTAIN_DAILY_CAP = 1;  // prizes/day. 0 = uncapped.

// caP'n's numbers. Read rarest-first against ONE uniform roll, so
// they stack rather than compete: 0–1% mythic, 1–4% legendary,
// 4–11% fable, 11–100% J'ynxi.
export const ODDS = { mythic: 0.01, legendary: 0.03, fable: 0.07 };
export const HIT_CHANCE = ODDS.mythic + ODDS.legendary + ODDS.fable; // 0.11

// ── ◆ THE COIN COMES BACK ─────────────────────────────────────
// caP'n, 2026-08-20, listing where Divine Crests come from: *"easter
// eggs, achievements, and challenges, maybe a couple other ways,
// fountain, V'ael randomly etc."* This is the fountain's half.
//
// ⭐ IT IS A SECOND, INDEPENDENT ROLL AND NOT A PRIZE ROW, and that
// is deliberate. Every row in FOUNTAIN_PRIZES is a CHECKLIST entry —
// `ownedPrizeIds` / `poolFor` / `remainingCount` all assume a prize is
// a thing you can own exactly once and that owning it removes it from
// the pool. A currency cannot be owned once. Dropping a `kind:
// "crests"` row into that table would have quietly broken the pool
// arithmetic, the "N left" counter and the fall-through that
// guarantees a hit is never unpaid.
//
// So it rides beside the cosmetic roll, on its own die, and it does
// NOT touch the daily cap: the cap exists to stop a player farming
// the checklist in one sitting, and a Crest is not on the checklist.
// A capped player still gets nothing, because the cap is checked
// before the charge and they are never billed.
//
// ⚠ ONE ◆ AND NOT MORE. THE THREE RANKS price the hardest achievement
// in the game at 5. A repeatable faucet must sit UNDER the cheapest
// one-time one or the one-time ones stop meaning anything — this is
// the only repeatable ◆ source in Terror and it is priced like it.
// ⚠ AND IT HAS ITS OWN DAILY CAP, WHICH IT DID NOT AT FIRST AND HAD TO.
// FOUNTAIN_DAILY_CAP is enforced through `fountain/lastWinDay`, and
// `tossCoin` only writes that stamp AFTER A PRIZE GRANT. A player who
// owns every prize returns early on `exhausted` forever, so the stamp
// is never written and the cap never trips again. At 15 ◕ a toss and a
// 5% roll that is 300 ◕ per ◆ — and Chocolate Coins are infinitely
// farmable (75 a free-play win, no cap), so roughly four NPC wins bought
// one Divine Crest, forever, with no ceiling. The Foil Vault and the
// mythic coin skin would have been grindable out of the water.
//
// So the Crest carries its OWN stamp on its OWN key. One ◆ a day from
// the fountain, whether or not the prize checklist is finished, whether
// or not the prize roll hit. It stays the only repeatable ◆ source in
// Terror and it is now a trickle rather than a tap.
export const CREST_CHANCE = 0.05;   // per toss, independent of the prize roll
export const CREST_PAYOUT = 1;      // ◆
export const CREST_DAILY_CAP = 1;   // ◆ per local day. 0 = uncapped.

// Rarest → commonest. Used for the fall-through below, so the order
// is load-bearing, not decorative.
export const RARITY_ORDER = ["mythic", "legendary", "fable"];

export const RARITY_META = {
  fable:     { label: "FABLE",     color: "#8fb8e8" },
  legendary: { label: "LEGENDARY", color: "#e8b84b" },
  mythic:    { label: "MYTHIC",    color: "#f43f5e" },
};

// ── THE POOL ──────────────────────────────────────────────────
// Seeded with every cosmetic that already exists and costs money,
// so the fountain is playable the moment it deploys rather than
// waiting on art. The free defaults (Gilded Obsidian, the Lyt
// pulse) are excluded — everyone owns them, so they would be a
// prize that is never in anyone's pool.
//
// `kind` picks the granter. `ref` is the id in that system's own
// catalog — NOT a copy of its name or rarity, which stay owned by
// RoomBackdrop.js and DrawPulse.js. The `name` here is for the
// reveal card only.
export const FOUNTAIN_PRIZES = [
  // ── MYTHIC (1%) ──
  { id: "bd_emperiums_eye",    kind: "backdrop", ref: "emperiums_eye",    rarity: "mythic",    name: "The Minerium's Eye" },
  { id: "bd_jynxi_unsealed",   kind: "backdrop", ref: "jynxi_unsealed",   rarity: "mythic",    name: "J'ynxi, Unsealed" },
  { id: "bd_necropolis_bloom", kind: "backdrop", ref: "necropolis_bloom", rarity: "mythic",    name: "Necropolis Bloom" },
  { id: "bd_solrend_eternal",  kind: "backdrop", ref: "solrend_eternal",  rarity: "mythic",    name: "Solrend Eternal" },

  // ── LEGENDARY (3%) ──
  { id: "bd_gilded_procession", kind: "backdrop", ref: "gilded_procession", rarity: "legendary", name: "Gilded Procession" },
  { id: "bd_wyrmfire_gate",     kind: "backdrop", ref: "wyrmfire_gate",     rarity: "legendary", name: "Wyrmfire Gate" },
  { id: "bd_grimoire_whisper",  kind: "backdrop", ref: "grimoire_whisper",  rarity: "legendary", name: "Grimoire Whisper" },
  { id: "bd_stormbind",         kind: "backdrop", ref: "stormbind",         rarity: "legendary", name: "Stormbind" },

  // ── FABLE (7%) ──
  { id: "bd_emberline",       kind: "backdrop", ref: "emberline",       rarity: "fable", name: "Emberline" },
  { id: "bd_deep_vellum",     kind: "backdrop", ref: "deep_vellum",     rarity: "fable", name: "Deep Vellum" },
  { id: "bd_amethyst_drift",  kind: "backdrop", ref: "amethyst_drift",  rarity: "fable", name: "Amethyst Drift" },
  { id: "bd_serpents_coil",   kind: "backdrop", ref: "serpents_coil",   rarity: "fable", name: "Serpent's Coil" },
  { id: "bd_crest_rain",      kind: "backdrop", ref: "crest_rain",      rarity: "fable", name: "Crest Rain" },
  { id: "bd_jynxis_laughter", kind: "backdrop", ref: "jynxis_laughter", rarity: "fable", name: "J'ynxi's Laughter" },
  { id: "bd_molten_sigil",    kind: "backdrop", ref: "molten_sigil",    rarity: "fable", name: "Molten Sigil" },
  { id: "bd_shatterlight",    kind: "backdrop", ref: "shatterlight",    rarity: "fable", name: "Shatterlight" },
  { id: "dp_khao_bloom",      kind: "pulse",    ref: "khao_bloom",      rarity: "fable", name: "Khao Bloom" },
  { id: "dp_deepglass",       kind: "pulse",    ref: "deepglass",       rarity: "fable", name: "Deepglass" },
  { id: "dp_emberfall",       kind: "pulse",    ref: "emberfall",       rarity: "fable", name: "Emberfall" },
  { id: "dp_jynxis_grin",     kind: "pulse",    ref: "jynxis_grin",     rarity: "fable", name: "J'ynxi's Grin" },
];

export const KIND_LABEL = { backdrop: "Backdrop", pulse: "Draw Pulse", holo: "Holo", companion: "Companion" };

// ── J'YNXI'S FAKE CARD ────────────────────────────────────────
// The miss is the feature. 89% of tosses land here, so it needs
// more than one line or the joke dies on toss four.
export const JYNXI_TAUNTS = [
  "Better luck next time. Hehe.",
  "Oh! So close. It wasn't close.",
  "The fountain thanks you for your generous donation.",
  "I felt something. It was your coin. Leaving.",
  "Wish harder. That's the whole mechanic. Wish harder.",
  "That one's mine now. They're all mine, really.",
  "A coin! For me? You shouldn't have. And yet.",
  "Nothing. Again. I do love this part.",
  "The water says no. The water says it a lot.",
  "Try again. I'll wait. I'm extremely patient about this.",
  "Something stirred down there. It went back to sleep.",
  "Statistically, this was the likely outcome. Statistically.",
];

export const pickTaunt = () => JYNXI_TAUNTS[Math.floor(Math.random() * JYNXI_TAUNTS.length)];

// ── DAY KEY ───────────────────────────────────────────────────
// Local midnight, not UTC — a daily reset should land at the
// player's midnight, not at one that means nothing to them. Dial:
// swap to toISOString().slice(0,10) for a world-synchronised day.
export function fountainDayKey(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ── OWNERSHIP ─────────────────────────────────────────────────
/** Prize ids the player already holds, read from each system's real unlock path. */
export function ownedPrizeIds(backdropUnlocks, pulseUnlocks) {
  const bd = backdropUnlocks || {};
  const dp = pulseUnlocks || {};
  return new Set(
    FOUNTAIN_PRIZES
      .filter(p => (p.kind === "backdrop" && bd[p.ref]) || (p.kind === "pulse" && dp[p.ref]))
      .map(p => p.id)
  );
}

export const poolFor = (rarity, owned) =>
  FOUNTAIN_PRIZES.filter(p => p.rarity === rarity && !owned.has(p.id));

export const remainingCount = (owned) => FOUNTAIN_PRIZES.filter(p => !owned.has(p.id)).length;

// ── THE ROLL ──────────────────────────────────────────────────
/**
 * Resolve one toss. `roll` is a uniform 0..1 supplied by the caller
 * so the decision is made ONCE and cannot be re-rolled by a retry.
 *
 * FALL-THROUGH: a hit never goes unpaid. If the rolled tier is
 * exhausted we take the nearest tier that still has stock — down
 * first (you rolled Mythic, you get the best thing left), then up
 * (if the only prizes remaining are rarer than what you rolled,
 * refusing to pay a won roll would be the worst feeling in the game).
 */
export function resolveToss(roll, owned) {
  let tier = null;
  if (roll < ODDS.mythic) tier = "mythic";
  else if (roll < ODDS.mythic + ODDS.legendary) tier = "legendary";
  else if (roll < HIT_CHANCE) tier = "fable";
  if (!tier) return { hit: false, rolledTier: null, prize: null };

  const i = RARITY_ORDER.indexOf(tier);
  const search = [
    ...RARITY_ORDER.slice(i),                    // rolled tier, then downward
    ...RARITY_ORDER.slice(0, i).reverse(),       // then upward from nearest
  ];
  for (const t of search) {
    const pool = poolFor(t, owned);
    if (pool.length) {
      const prize = pool[Math.floor(Math.random() * pool.length)];
      return { hit: true, rolledTier: tier, prize, substituted: t !== tier };
    }
  }
  // Nothing left anywhere — the checklist is complete.
  return { hit: false, rolledTier: tier, prize: null, exhausted: true };
}

// ── THE GRANT ─────────────────────────────────────────────────
// Dispatches on `kind`. New prize types are a case here and a row
// in the table above; nothing else in the game needs to know.
async function grantPrize(uid, prize) {
  if (prize.kind === "backdrop") return unlockBackdrop(uid, prize.ref, 0, "fountain");
  if (prize.kind === "pulse")    return unlockDrawPulse(uid, prize.ref, 0, "fountain");
  console.warn("[fountain] no granter for prize kind:", prize.kind);
  return { ok: false, reason: "no-granter" };
}

/**
 * Toss one coin.
 *
 * Order is deliberate and matches the economy law's GRANT BEFORE
 * FLAG: charge, roll, grant, and only then stamp the day. A crash
 * between grant and stamp costs the player an extra prize today,
 * which is recoverable and pleasant. The reverse would eat the
 * prize they just watched come out of the water.
 *
 * Returns one of:
 *   { ok:false, reason:"insufficient-coins" | "daily-cap" | "bad-args" }
 *   { ok:true,  hit:false, exhausted?:true, taunt,  crests }
 *   { ok:true,  hit:true,  prize, rolledTier, substituted, crests }
 *
 * `crests` is 0 or CREST_PAYOUT and is independent of `hit` — it has
 * its own die. See THE COIN COMES BACK.
 */
export async function tossCoin(uid, owned, opts = {}) {
  if (!uid) return { ok: false, reason: "bad-args" };
  const cost = opts.cost ?? FOUNTAIN_COST;
  const today = fountainDayKey();

  // Daily cap — checked before the charge so a capped player is
  // never billed for a toss that could not have paid out.
  if (FOUNTAIN_DAILY_CAP > 0) {
    const stamp = await get(ref(db, `profiles/${uid}/fountain/lastWinDay`));
    if (stamp.exists() && stamp.val() === today) {
      return { ok: false, reason: "daily-cap" };
    }
  }

  // Charge — the canonical atomic debit. Aborts untouched if short,
  // so there is no read-then-write window to lose a coin in.
  const paid = await spendCoins(uid, cost);
  if (!paid) return { ok: false, reason: "insufficient-coins" };

  const result = resolveToss(Math.random(), owned instanceof Set ? owned : new Set(owned || []));

  // ── ◆ THE COIN COMES BACK ────────────────────────────────────
  // Its own die, rolled whether the prize roll hit or missed. See
  // THE COIN COMES BACK above for why it is not a prize row. The
  // `reason` is deliberately per-day and not per-toss: a repeatable
  // faucet still has to be nameable in an audit row, and "the
  // fountain, on this date" is the honest name for it.
  let crests = 0;
  if (Math.random() < CREST_CHANCE) {
    try {
      // The cap is a TRANSACTION on its own stamp, not a read-then-write:
      // two tabs tossing at once would both pass a bare read and both
      // pour. `undefined` aborts, and an aborted transaction is the
      // whole cap.
      let mayPour = true;
      if (CREST_DAILY_CAP > 0) {
        const dayRef = ref(db, `profiles/${uid}/fountain/lastCrestDay`);
        const tx = await runTransaction(dayRef, (cur) => (cur === today ? undefined : today));
        mayPour = !!tx.committed;
      }
      if (mayPour) {
        await grantCrests(uid, CREST_PAYOUT, `fountain:${today}`);
        crests = CREST_PAYOUT;
      }
    } catch (e) {
      // The toss is not a Crest transaction. It never fails over one.
      console.warn("[fountain] Crest pour failed:", e && e.message);
    }
  }

  if (!result.hit) {
    return { ok: true, hit: false, exhausted: !!result.exhausted, taunt: pickTaunt(), crests };
  }

  const granted = await grantPrize(uid, result.prize);
  if (!granted?.ok) {
    // The coin is spent and the prize did not land. Say so plainly
    // rather than showing a reveal for something they don't own.
    return { ok: false, reason: granted?.reason || "grant-failed" };
  }

  // Flag last, and only on a real grant.
  await set(ref(db, `profiles/${uid}/fountain/lastWinDay`), today);
  await runTransaction(
    ref(db, `profiles/${uid}/fountain/won/${result.prize.id}`),
    (current) => current || { ts: Date.now(), rarity: result.prize.rarity }
  );

  return { ok: true, hit: true, prize: result.prize, rolledTier: result.rolledTier, substituted: !!result.substituted, crests };
}

const fountain = {
  FOUNTAIN_COST, FOUNTAIN_DAILY_CAP, ODDS, HIT_CHANCE, CREST_CHANCE, CREST_PAYOUT, CREST_DAILY_CAP,
  RARITY_ORDER, RARITY_META, FOUNTAIN_PRIZES, KIND_LABEL,
  JYNXI_TAUNTS, pickTaunt, fountainDayKey,
  ownedPrizeIds, poolFor, remainingCount, resolveToss, tossCoin,
};
export default fountain;
